import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';

import { useDebounce } from 'hooks/useDebounce';
import { RxnormCoding, RxnormColumnCoding } from 'models/HighCostMedicationListItem';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';
import CheckIcon from 'svg/CheckIcon';

import { ExclamationIcon, ingredientsLabel, matchedAsLabel, SuggestedNames, SuggestionChip } from './rxnormStatus';

type Names = { genericName: string; brandName: string };
type NameValues = { genericName?: string | null; brandName?: string | null };

const trimNames = (names: NameValues): Names => ({
  genericName: (names.genericName ?? '').trim(),
  brandName: (names.brandName ?? '').trim(),
});

const sameNames = (a: Names, b: Names) => a.genericName === b.genericName && a.brandName === b.brandName;

// Either name is checked on its own: a brand typed before its generic still gets a result.
const hasName = (names: Names) => !!(names.genericName || names.brandName);

type Options = {
  preview: (params: { genericName: string; brandName: string | null }) => Promise<RxnormCoding>;
  // Keeps the admin and customer endpoints' results apart in the cache.
  scope: string;
  values: NameValues;
  // An edit opens already checked against the names it was saved with.
  initialNames: NameValues;
};

// Checks the two name fields through the coding preview as the editor leaves one or stops typing.
// Nothing is saved. A result shows only while the fields still hold the names it was computed
// for; a failed request (the api not yet serving the preview) shows nothing.
export function useRxnormNameCheck({ preview, scope, values, initialNames }: Options) {
  const [committed, setCommitted] = useState(() => trimNames(initialNames));
  // Tabbing from one name field through the other commits twice; the debounce sends one request.
  const names = useDebounce(committed, 300);

  const { data, isFetching, refetch } = useQuery({
    queryKey: ['highCostMedicationListItemCodingPreview', scope, names],
    queryFn: () => preview({ genericName: names.genericName, brandName: names.brandName || null }),
    enabled: hasName(names),
    // A coding is a pure function of the two names, so a repeat of the same pair need not refetch.
    // "olio-ai couldn't answer" (state null) is not a coding, and is asked again.
    staleTime: (query) => (query.state.data?.state === null ? 0 : Infinity),
  });

  const current = trimNames(values);
  // Debounced per field: a fresh `current` object every render would restart one shared timer.
  const typedGeneric = useDebounce(current.genericName, 800);
  const typedBrand = useDebounce(current.brandName, 800);
  useEffect(() => {
    const typed = { genericName: typedGeneric, brandName: typedBrand };
    setCommitted((previous) => (sameNames(previous, typed) ? previous : typed));
  }, [typedGeneric, typedBrand]);

  const isCurrent = hasName(current) && sameNames(current, names);

  return {
    result: isCurrent ? data : undefined,
    checking: hasName(current) && sameNames(current, committed) && (!sameNames(committed, names) || isFetching),
    commit: (override: Partial<Names> = {}) => {
      const next = trimNames({ ...current, ...override });
      // The same names keep the same query key, so a retry has to be asked for.
      if (isCurrent && data?.state === null && sameNames(next, names)) refetch();
      setCommitted(next);
    },
  };
}

export type RxnormNameCheck = ReturnType<typeof useRxnormNameCheck>;

type ColumnResultProps = {
  check: RxnormNameCheck;
  column: 'generic' | 'brand';
  // The field's current text, quoted back in the notice.
  name: string;
  onChoose: (name: string) => void;
  // Replaces a brand name typed as the generic with the generic it coded to.
  onUseGeneric?: (name: string) => void;
};

const STILL_CHECKED_BY = {
  generic: 'This medication will still be checked by its brand name.',
  brand: 'This medication will still be checked by its generic name.',
};

const isBrandAsGeneric = (coding: RxnormColumnCoding | null | undefined, column: 'generic' | 'brand') =>
  column === 'generic' && coding?.status === 'coded' && !!coding.brandName;

const isGenericAsBrand = (coding: RxnormColumnCoding | null | undefined, column: 'generic' | 'brand') =>
  column === 'brand' && coding?.status === 'coded' && !!coding.genericName;

// Whether a name field's border should mark it: not validated, or a name typed in the other's field.
export const rxnormFieldFlagged = ({ result }: RxnormNameCheck, column: 'generic' | 'brand') =>
  result?.[column]?.status === 'not_found' ||
  isBrandAsGeneric(result?.[column], column) ||
  isGenericAsBrand(result?.[column], column);

export const flaggedInputStyle = { borderColor: colors.orangeHover };

// What one name field validated as, or that it could not be, with up to three suggestions. Choosing
// a suggestion fills the field; the editor still saves.
export function RxnormColumnResult({ check, column, name, onChoose, onUseGeneric }: ColumnResultProps) {
  const { result } = check;

  if (!result) {
    if (!name.trim()) return null;
    // The line is held while the field is edited, so "Checking…" appearing on the blur from a
    // mousedown on Save moves nothing below it: a shifted button would swallow the mouseup.
    return <Muted style={check.checking ? undefined : { visibility: 'hidden' }}>Checking&hellip;</Muted>;
  }
  // olio-ai could not answer: the preview comes back with every field null.
  if (result.state === null) return name.trim() ? <Muted>Couldn&rsquo;t check this name right now</Muted> : null;

  const coding = result[column];
  if (!coding) return null;

  if (isBrandAsGeneric(coding, column)) {
    return (
      <Notice data-testid='rxnorm-generic-is-brand'>
        <ExclamationIcon color={colors.orangeExtraDark} />
        <NoticeBody>
          <NoticeTitle>&lsquo;{name.trim()}&rsquo; is a brand name, not a generic name</NoticeTitle>
          <BodySmall>
            This medication will still be checked as {matchedAsLabel(coding.matchedAs)}. Use the generic name instead.
          </BodySmall>
          {onUseGeneric && (
            <SuggestedNames>
              {coding.matchedAs.map((reading) => {
                const label = ingredientsLabel(reading);
                return <SuggestionChip key={label} name={label} onClick={() => onUseGeneric(label)} />;
              })}
            </SuggestedNames>
          )}
        </NoticeBody>
      </Notice>
    );
  }

  // A generic has no single brand to offer, so this only warns.
  if (isGenericAsBrand(coding, column)) {
    return (
      <Notice data-testid='rxnorm-brand-is-generic'>
        <ExclamationIcon color={colors.orangeExtraDark} />
        <NoticeBody>
          <NoticeTitle>&lsquo;{name.trim()}&rsquo; is a generic name, not a brand name</NoticeTitle>
          <BodySmall>
            This medication will still be checked as {matchedAsLabel(coding.matchedAs)}. Leave the brand name blank if
            it has no brand.
          </BodySmall>
        </NoticeBody>
      </Notice>
    );
  }

  if (coding.status === 'coded') {
    return (
      <Validated>
        <CheckIcon width={13} height={10} fill={colors.blue100} />
        Validated as &lsquo;{matchedAsLabel(coding.matchedAs)}&rsquo;
      </Validated>
    );
  }

  const suggestions = coding.suggestions.slice(0, 3);

  return (
    <Notice data-testid={`rxnorm-${column}-not-found`}>
      <ExclamationIcon color={colors.orangeExtraDark} />
      <NoticeBody>
        <NoticeTitle>
          Olio couldn&rsquo;t validate the {column === 'brand' ? 'brand name' : 'name'} &lsquo;{name.trim()}&rsquo;
        </NoticeTitle>
        <BodySmall>
          {result.state === 'not_recognized'
            ? 'This medication requires a matching name with RxNorm (Olio’s validation provider) to be checked.'
            : STILL_CHECKED_BY[column]}
        </BodySmall>
        {suggestions.length > 0 && (
          <SuggestedNames>
            {suggestions.map((suggestion) => (
              <SuggestionChip key={suggestion.rxcui} name={suggestion.name} onClick={() => onChoose(suggestion.name)} />
            ))}
          </SuggestedNames>
        )}
      </NoticeBody>
    </Notice>
  );
}

type ConflictProps = {
  check: RxnormNameCheck;
  brandName: string;
  onSetGeneric: (name: string) => void;
};

// Both names code, to different medications. The row still matches either; offering the brand's
// ingredients as the generic is the usual fix.
export function RxnormConflictNotice({ check, brandName, onSetGeneric }: ConflictProps) {
  const { result } = check;
  if (result?.state !== 'generic_brand_conflict' || !result.brand) return null;

  return (
    <Notice data-testid='rxnorm-conflict'>
      <ExclamationIcon color={colors.orangeExtraDark} />
      <NoticeBody>
        <NoticeTitle>Generic and brand name point to different medications</NoticeTitle>
        <BodySmall>
          Referrals mentioning either one will flag this row. If {brandName.trim()} is what&rsquo;s priced, use its full
          generic name.
        </BodySmall>
        <SuggestedNames>
          {result.brand.matchedAs.map((reading) => {
            const label = ingredientsLabel(reading);
            return <SuggestionChip key={label} name={label} onClick={() => onSetGeneric(label)} />;
          })}
        </SuggestedNames>
      </NoticeBody>
    </Notice>
  );
}

const Muted = styled.span`
  margin-top: 8px;
  font-size: 12px;
  color: ${colors.black50};
`;

const Validated = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  font-size: 12px;
  color: ${colors.black75};
`;

const Notice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-top: 8px;
  padding: 16px;
  border-radius: 4px;
  background: ${colors.orange10};

  & > svg {
    margin-top: 2px;
  }
`;

const NoticeBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const NoticeTitle = styled(BodySmallBold)`
  color: ${colors.orangeExtraDark};
`;
